import * as React from 'react';
import { AlertTriangle, Check, ClipboardPaste, Layers, Search, X } from 'lucide-react';
import { useMapa } from '@/hooks/consultas';
import {
  usePreviaCiclico,
  useRuasCiclico,
  type CriterioCiclico,
  type SelecaoCiclica,
  type Sobreposicao,
} from '@/hooks/inventario';
import { cn, compararNatural, numero, plural } from '@/lib/utils';
import { useDebounce } from '@/hooks/useDebounce';
import { Botao } from '@/componentes/ui/botao';
import { Esqueleto } from '@/componentes/ui/primitivos';
import { CampoProduto, type ProdutoEscolhido } from '@/componentes/transferencia/CampoProduto';
import { haQuanto } from './semaforo';

/**
 * Escolher o que o inventário CÍCLICO vai contar — e ver, antes de gravar,
 * quantos escopos e endereços isso vira.
 *
 *   Por rua  um escopo por rua. As ruas vêm ordenadas pela última vez que
 *            foram inventariadas (nunca primeiro): é a vez delas no ciclo.
 *   Por SKU  um escopo com as vagas onde os SKUs estão. A VAGA INTEIRA é
 *            contada: o fechamento do PBS considera todo produto com saldo no
 *            endereço, e o que não fosse contado seria zerado.
 *
 * Só escolhe e mostra a prévia; quem grava é o botão do componente pai.
 */
export function GeradorCiclico({
  centro,
  criterio,
  aoMudar,
}: {
  centro: number;
  criterio: CriterioCiclico;
  /** `null` enquanto nada válido foi escolhido. */
  aoMudar: (selecao: SelecaoCiclica | null, grupos: number) => void;
}): JSX.Element {
  const [ruas, definirRuas] = React.useState<Set<string>>(new Set());
  const [skus, definirSkus] = React.useState<ProdutoEscolhido[]>([]);

  const selecao = React.useMemo<SelecaoCiclica | null>(() => {
    if (criterio === 'RUA') return ruas.size > 0 ? { criterio, ruas: [...ruas].sort(compararNatural) } : null;
    return skus.length > 0 ? { criterio, produtos: skus.map((s) => s.produto) } : null;
  }, [criterio, ruas, skus]);
  const atrasada = useDebounce(selecao, 300);
  const previa = usePreviaCiclico(centro, atrasada);
  const grupos = previa.data?.grupos ?? [];
  const validos = selecao === null ? 0 : grupos.filter((g) => g.enderecos > 0).length;

  React.useEffect(() => {
    aoMudar(selecao, selecao === null || previa.isFetching || atrasada !== selecao ? 0 : validos);
    // `aoMudar` é callback do pai; o que dispara é a seleção e a prévia.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selecao, validos, previa.isFetching, atrasada]);

  return (
    <div className="space-y-3">
      {criterio === 'RUA' ? (
        <EscolherRuas centro={centro} escolhidas={ruas} aoMudar={definirRuas} />
      ) : (
        <EscolherSkus escolhidos={skus} aoMudar={definirSkus} />
      )}

      {selecao ? (
        <div className="rounded-xl border border-borda bg-superficie2 px-3 py-2.5 text-[12.5px]">
          <p
            className={cn(
              'mb-1.5 flex items-center gap-1.5 font-semibold',
              !previa.isLoading && validos === 0 && 'text-status-atencao',
            )}
          >
            {!previa.isLoading && validos === 0 ? (
              <AlertTriangle className="h-4 w-4" />
            ) : (
              <Layers className="h-4 w-4 text-primaria" />
            )}
            {previa.isLoading
              ? 'Calculando…'
              : validos === 0
                ? criterio === 'SKU'
                  ? 'Nada a gerar: nenhum dos SKUs tem vaga neste centro'
                  : 'Nada a gerar: as ruas escolhidas não têm endereço ativo'
                : `Vai gerar ${numero(validos)} ${plural(validos, 'escopo', 'escopos')}`}
          </p>
          {previa.isError ? (
            <p className="text-status-divergencia">
              {previa.error instanceof Error ? previa.error.message : 'Não foi possível calcular a prévia.'}
            </p>
          ) : (
            <ul className="flex flex-wrap gap-1.5">
              {grupos.map((g) => (
                <li
                  key={g.rotulo}
                  className={cn(
                    'rounded-md border px-2 py-0.5 text-[12px]',
                    g.enderecos > 0
                      ? 'border-borda bg-superficie'
                      : 'border-status-atencao/50 bg-status-atencaoFundo text-status-atencao',
                  )}
                >
                  <b>{g.rotulo}</b> · {g.enderecos > 0 ? `${numero(g.enderecos)} end.` : 'sem endereço — fica de fora'}
                  {g.emEscopoAberto.length > 0 ? (
                    <span className="ml-1 font-semibold text-status-atencao">
                      · ⚠ {numero(g.emEscopoAberto.reduce((t, s) => t + s.enderecos, 0))} já em escopo aberto
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
          {grupos.some((g) => g.emEscopoAberto.length > 0) ? (
            <div className="mt-2 flex items-start gap-1.5 rounded-lg border border-status-atencao/40 bg-status-atencaoFundo px-2.5 py-2 text-status-atencao">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                <b>Endereços que já estão em escopo aberto:</b>{' '}
                {textoSobreposicao(grupos.flatMap((g) => g.emEscopoAberto))}. Se os dois escopos forem integrados, o
                mesmo endereço é ajustado duas vezes. Dá para gerar assim mesmo (como no PBS) — confira antes de
                integrar.
              </span>
            </div>
          ) : null}
          {criterio === 'SKU' && grupos[0] && grupos[0].semVaga.length > 0 ? (
            <p className="mt-1.5 flex items-start gap-1.5 text-status-atencao">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              Sem vaga neste centro — sem saldo em endereço e sem endereço cadastrado (PRODUTOS_LOCAIS), ficam de
              fora: {grupos[0].semVaga.join(', ')}
            </p>
          ) : null}
          {criterio === 'SKU' ? (
            <p className="mt-1.5 text-textoFraco">
              A equipe conta a <b className="text-texto">vaga inteira</b>: todos os produtos desses endereços entram no
              escopo. É o que o fechamento do PBS exige — produto da vaga que não fosse contado seria zerado.
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** "9 end. no #2157 (inv. 12), 2 no #2154 (inv. 12)" — juntando o mesmo escopo. */
function textoSobreposicao(lista: Sobreposicao[]): string {
  const por = new Map<number, Sobreposicao>();
  for (const s of lista) {
    const a = por.get(s.escopo);
    por.set(s.escopo, a ? { ...a, enderecos: a.enderecos + s.enderecos } : { ...s });
  }
  return [...por.values()]
    .sort((a, b) => b.enderecos - a.enderecos)
    .map(
      (s) =>
        `${numero(s.enderecos)} end. no #${s.escopo}${s.inventario !== null ? ` (inv. ${s.inventario})` : ' (do PBS)'}`,
    )
    .join(', ');
}

function EscolherRuas({
  centro,
  escolhidas,
  aoMudar,
}: {
  centro: number;
  escolhidas: Set<string>;
  aoMudar: (s: Set<string>) => void;
}): JSX.Element {
  const ruas = useRuasCiclico(centro);
  const mapa = useMapa(centro);
  const [busca, definirBusca] = React.useState('');

  // Endereços com saldo por rua, do mapa do centro (já em cache).
  const comSaldo = React.useMemo(() => {
    const m = new Map<string, number>();
    for (const p of mapa.data?.posicoes ?? []) {
      if ((p.saldo ?? 0) <= 0) continue;
      const r = p.corredor.trim().toUpperCase();
      m.set(r, (m.get(r) ?? 0) + 1);
    }
    return m;
  }, [mapa.data]);

  // Nunca inventariada primeiro, depois a mais antiga: é a vez dela no ciclo.
  const lista = React.useMemo(() => {
    const termo = busca.trim().toUpperCase();
    return [...(ruas.data?.ruas ?? [])]
      .filter((r) => !termo || r.rua.includes(termo))
      .sort(
        (a, b) =>
          (a.ultimaIntegracao ?? '').localeCompare(b.ultimaIntegracao ?? '') || compararNatural(a.rua, b.rua),
      );
  }, [ruas.data, busca]);

  const alternar = (rua: string): void => {
    const n = new Set(escolhidas);
    if (n.has(rua)) n.delete(rua);
    else n.add(rua);
    aoMudar(n);
  };

  if (ruas.isLoading) return <Esqueleto className="h-40" />;
  if (ruas.isError) {
    return <p className="text-sm text-status-divergencia">Não foi possível carregar as ruas do centro.</p>;
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[180px] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-textoFraco" />
          <input
            value={busca}
            onChange={(e) => definirBusca(e.target.value)}
            placeholder="Filtrar ruas"
            aria-label="Filtrar ruas"
            className="min-h-[34px] w-full rounded-lg border border-borda bg-superficie pl-8 pr-2 text-[13px]"
          />
        </div>
        <span className="text-[12px] text-textoFraco">
          {numero(escolhidas.size)} de {numero(ruas.data?.ruas.length ?? 0)} ruas · as nunca inventariadas e as mais
          antigas primeiro
        </span>
        {escolhidas.size > 0 ? (
          <Botao type="button" variante="fantasma" tamanho="micro" onClick={() => aoMudar(new Set())}>
            <X className="h-3.5 w-3.5" />
            Limpar
          </Botao>
        ) : null}
      </div>
      <div className="grid max-h-[300px] gap-1.5 overflow-y-auto pr-1 sm:grid-cols-2 lg:grid-cols-3">
        {lista.map((r) => {
          const ativa = escolhidas.has(r.rua);
          return (
            <button
              key={r.rua}
              type="button"
              onClick={() => alternar(r.rua)}
              aria-pressed={ativa}
              className={cn(
                'flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-left transition-colors',
                ativa ? 'border-primaria bg-primaria-suave' : 'border-borda bg-superficie hover:border-bordaForte',
              )}
            >
              <span
                className={cn(
                  'grid h-4 w-4 shrink-0 place-items-center rounded border',
                  ativa ? 'border-primaria bg-primaria text-white' : 'border-bordaForte',
                )}
              >
                {ativa ? <Check className="h-3 w-3" /> : null}
              </span>
              <span className="min-w-0 flex-1 leading-tight">
                <span className={cn('block font-mono text-[13px] font-bold', ativa && 'text-primaria')}>{r.rua}</span>
                <span className="tabular block text-[11px] text-textoFraco">
                  {numero(r.enderecos)} end.
                  {comSaldo.has(r.rua) ? ` · ${numero(comSaldo.get(r.rua) ?? 0)} com saldo` : ''}
                </span>
                {r.emEscopoAberto.length > 0 ? (
                  <span
                    className="block truncate text-[10.5px] font-semibold text-status-atencao"
                    title={`Já em escopo aberto: ${textoSobreposicao(r.emEscopoAberto)}`}
                  >
                    ⚠ {numero(r.emEscopoAberto.reduce((t, s) => t + s.enderecos, 0))} end. em escopo aberto (
                    {r.emEscopoAberto.map((s) => `#${s.escopo}`).join(', ')})
                  </span>
                ) : null}
              </span>
              <span
                className={cn(
                  'shrink-0 text-right text-[10.5px] leading-tight',
                  r.ultimaIntegracao ? 'text-textoFraco' : 'font-semibold text-status-atencao',
                )}
                title="Última integração de inventário com endereço nesta rua"
              >
                {r.ultimaIntegracao ? (
                  <>
                    inventariada
                    <br />
                    {haQuanto(r.ultimaIntegracao)}
                  </>
                ) : (
                  'nunca'
                )}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function EscolherSkus({
  escolhidos,
  aoMudar,
}: {
  escolhidos: ProdutoEscolhido[];
  aoMudar: (s: ProdutoEscolhido[]) => void;
}): JSX.Element {
  const [colando, definirColando] = React.useState(false);
  const [texto, definirTexto] = React.useState('');

  const adicionar = (novos: ProdutoEscolhido[]): void => {
    const ja = new Set(escolhidos.map((s) => s.produto));
    aoMudar([...escolhidos, ...novos.filter((n) => !ja.has(n.produto))]);
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-[260px] flex-1">
          <CampoProduto
            valor={null}
            rotuloAcessivel="Adicionar SKU"
            aoEscolher={(p) => adicionar([p])}
            aoLimpar={() => undefined}
          />
        </div>
        <Botao type="button" variante="fantasma" tamanho="pequeno" onClick={() => definirColando((a) => !a)}>
          <ClipboardPaste className="h-3.5 w-3.5" />
          Colar lista
        </Botao>
      </div>
      {colando ? (
        <div className="space-y-1.5">
          <textarea
            value={texto}
            onChange={(e) => definirTexto(e.target.value)}
            rows={3}
            placeholder="Códigos dos SKUs, um por linha (ou separados por espaço, vírgula ou ;)"
            className="w-full rounded-lg border border-borda bg-superficie p-2 font-mono text-[12.5px]"
          />
          <Botao
            type="button"
            variante="secundaria"
            tamanho="micro"
            disabled={texto.trim() === ''}
            onClick={() => {
              const codigos = [...new Set(texto.split(/[^0-9]+/).filter(Boolean).map(Number))];
              adicionar(codigos.map((produto) => ({ produto, descricao: null })));
              definirTexto('');
              definirColando(false);
            }}
          >
            Adicionar códigos
          </Botao>
        </div>
      ) : null}
      {escolhidos.length > 0 ? (
        <ul className="flex max-h-[180px] flex-wrap gap-1.5 overflow-y-auto">
          {escolhidos.map((s) => (
            <li
              key={s.produto}
              className="inline-flex max-w-[320px] items-center gap-1.5 rounded-md border border-borda bg-superficie px-2 py-0.5 text-[12px]"
            >
              <b className="tabular font-mono">{s.produto}</b>
              {s.descricao ? <span className="truncate text-textoFraco">{s.descricao}</span> : null}
              <button
                type="button"
                onClick={() => aoMudar(escolhidos.filter((x) => x.produto !== s.produto))}
                className="rounded p-0.5 text-textoFraco hover:text-texto"
                aria-label={`Tirar ${s.produto}`}
              >
                <X className="h-3 w-3" />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[12px] text-textoFraco">Busque pelo código, EAN ou descrição, ou cole uma lista de códigos.</p>
      )}
    </div>
  );
}
